import { useEffect, useMemo, useState } from "react";
import { Gem, Ticket } from "lucide-react";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { GameModal } from "../../components/GameModal";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";

interface GemsSummary {
  member: null | {
    member_account: string;
    status_label?: string;
  };
  assets: {
    gems: number;
    vouchers: number;
    glory_value: number;
  };
}

interface GemExchangeResult {
  balance: number | null;
  exchange_ref: string;
  voucher_type: string;
  duplicate?: boolean;
}

const EXCHANGE_TYPE = "newbie_bonus_red_packet";
const GEM_COST = 3;

export function GemsPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const queryAccount = searchParams.get("account") ?? searchParams.get("member_account") ?? "";
  const queryIdentityCode = searchParams.get("identity_code") ?? searchParams.get("phone_last3") ?? "";
  const account = queryAccount.trim() || localStorage.getItem("current_member_account") || localStorage.getItem("memberAccount") || "";
  const [summary, setSummary] = useState<GemsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modal, setModal] = useState<{ title: string; text: string; tone: "success" | "error" | "info"; nextHref?: string; nextText?: string } | null>(null);

  const gems = Number(summary?.assets?.gems ?? 0);
  const missingGems = Math.max(GEM_COST - gems, 0);
  const hasEnoughGems = gems >= GEM_COST;
  const canRedeem = hasEnoughGems && !isRedeeming;
  const idempotencyKey = useMemo(() => makeIdempotencyKey(), [gems]);

  async function loadSummary() {
    if (!account) return;
    setIsLoading(true);
    try {
      setSummary(await api<GemsSummary>(`/api/member/summary?member_account=${encodeURIComponent(account)}`));
    } catch {
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function redeem() {
    if (!account) {
      setModal({ title: "尚未登入", text: "請先回到活動大廳完成會員綁定。", tone: "error" });
      return;
    }
    if (!hasEnoughGems) {
      setModal({ title: "寶石碎片不足", text: `目前寶石碎片：${gems}，需要寶石碎片：${GEM_COST}，還差：${missingGems}。`, tone: "info" });
      return;
    }
    setIsRedeeming(true);
    try {
      const result = await api<GemExchangeResult>("/api/public/gem-exchange", {
        method: "POST",
        body: JSON.stringify({
          member_account: account,
          exchange_type: EXCHANGE_TYPE,
          idempotency_key: idempotencyKey
        })
      });
      await loadSummary();
      setModal({
        title: "兌換成功！",
        text: "兌換成功！紅包券已放入紅包券頁。",
        tone: "success",
        nextHref: "/red-packet",
        nextText: "前往紅包券頁"
      });
    } catch (err) {
      setModal({
        title: "兌換失敗",
        text: err instanceof Error ? err.message : "兌換失敗，請稍後再試。",
        tone: "error"
      });
    } finally {
      setIsRedeeming(false);
    }
  }

  function requestRedeem() {
    if (!account) {
      setModal({ title: "尚未登入", text: "請先回到首頁完成會員登入。", tone: "error" });
      return;
    }
    if (!hasEnoughGems) {
      setModal({ title: "寶石碎片不足", text: `目前寶石碎片：${gems}，需要寶石碎片：${GEM_COST}，還差：${missingGems}。`, tone: "info" });
      return;
    }
    setConfirmOpen(true);
  }

  useEffect(() => {
    if (queryAccount.trim()) {
      localStorage.setItem("current_member_account", queryAccount.trim());
      localStorage.setItem("memberAccount", queryAccount.trim());
    }
    if (queryIdentityCode.trim()) {
      localStorage.setItem("current_member_identity_code", queryIdentityCode.trim());
      localStorage.setItem("memberIdentityCode", queryIdentityCode.trim());
    }
    loadSummary();
  }, [account, queryAccount, queryIdentityCode]);

  return (
    <PublicLayout title="寶石碎片" subtitle="累積碎片，兌換龍來喬活動紅包券">
      <section className="mission-focus page-focus gems-hero-panel">
        <span className="hero-kicker">寶石兌換</span>
        <h2>寶石碎片兌換</h2>
        <p>用寶石碎片兌換紅包券，累積任務獎勵後就能直接在這裡兌換。</p>
        <div className="gems-balance-card">
          <span>目前寶石碎片</span>
          <strong>{isLoading ? "讀取中" : gems.toLocaleString("zh-TW")}</strong>
        </div>
      </section>

      <section className="exchange-grid gems-exchange-grid">
        <article className={`exchange-card active ${canRedeem ? "redeemable" : "locked"}`}>
          <div className="exchange-icon-orb">
            <Ticket size={26} />
          </div>
          <div className="exchange-card-main">
            <span className="hero-kicker">可兌換</span>
            <h3>新手加碼紅包券</h3>
            <strong>用 {GEM_COST} 枚寶石碎片兌換 1 張紅包券</strong>
            <p>{hasEnoughGems ? "可兌換 1 張紅包券" : "累積足夠寶石碎片後即可兌換紅包券"}</p>
            <p className="exchange-requirement-note">
              你目前有 {gems.toLocaleString("zh-TW")} 枚寶石碎片
              {hasEnoughGems ? "，可兌換 1 張紅包券" : `，還差 ${missingGems} 枚才能兌換`}
            </p>
          </div>
          <div className="exchange-card-action">
            <span className={`status-tag ${hasEnoughGems ? "active" : "locked"}`}>{hasEnoughGems ? "可兌換" : `還差 ${missingGems} 枚`}</span>
            <button className="primary-cta" disabled={!canRedeem} onClick={requestRedeem}>
              {isRedeeming ? "兌換中..." : hasEnoughGems ? `使用 ${GEM_COST} 枚寶石兌換紅包券` : "寶石碎片不足"}
            </button>
          </div>
        </article>

        <article className="exchange-card soon">
          <div className="exchange-icon-orb">
            <Gem size={24} />
          </div>
          <div className="exchange-card-main">
            <span className="hero-kicker">即將開放</span>
            <h3>更多寶石兌換</h3>
            <strong>後續開放</strong>
            <p>週耀紅包券、輪盤資格與高級紅包券會在後續版本接入。</p>
          </div>
          <div className="exchange-card-action">
            <span className="status-tag soon">尚未開放</span>
            <button className="secondary-cta" disabled>尚未開放</button>
          </div>
        </article>
      </section>

      {!account ? (
        <section className="login-gate">
          <span className="hero-kicker">請先登入</span>
          <h1>請先完成會員綁定</h1>
          <p>綁定後即可查看寶石碎片與兌換狀態。</p>
          <a className="primary-cta" href="/">回到活動大廳</a>
        </section>
      ) : null}

      <GameModal open={Boolean(modal)} title={modal?.title ?? ""} tone={modal?.tone ?? "info"} onClose={() => setModal(null)}>
        <p>{modal?.text}</p>
        {modal?.nextHref ? <a className="primary-cta modal-next-action" href={modal.nextHref}>{modal.nextText}</a> : null}
      </GameModal>
      <ConfirmDialog
        open={confirmOpen}
        title="確認兌換紅包券？"
        message={`將使用 ${GEM_COST} 枚寶石碎片兌換 1 張新手加碼紅包券。兌換成功後，可到紅包券頁開啟。`}
        confirmText="確認兌換"
        cancelText="取消"
        loading={isRedeeming}
        onConfirm={async () => {
          await redeem();
          setConfirmOpen(false);
        }}
        onCancel={() => {
          if (!isRedeeming) setConfirmOpen(false);
        }}
      />
    </PublicLayout>
  );
}

function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
